import { useState, useMemo, useRef, useEffect } from "react";
import { Caption, Title } from "../ui/typography";
import QuantityButton from "../ui/quantity-button";
import { Button } from "../ui/button";
import { Check, TriangleAlert } from "lucide-react";
import Link from "next/link";
import { useDispatch, useSelector } from "react-redux";
import Image from "next/image";
import { setSelectedProductId } from "@/app/redux/product-management/product-management-slice";
import { Flash } from "@/lib/SystemIcons";
import { formatToUSD } from "@/app/utils/utility";
import { setCartInputValue } from "@/app/redux/cart-management/cart-management-slice";

export default function ProductCard({ product, handleAddCart }) {
  const cartItem = useSelector((state) =>
    state?.cart?.cartDetails?.items?.find((item) => item.id === product?.id)
  );
  const localCartItems = useSelector(
    (state) => state?.cart?.cartDetails?.items
  );
  const cartInputValue = useSelector((state) => state?.cart?.cartInputValue);
  const productInCart = cartItem ? { ...cartItem } : null;

  const productCountInCart = useMemo(
    () => (!cartItem?.quantity ? 0 : cartItem?.quantity),
    [cartItem?.quantity]
  );

  const [quantity, setQuantity] = useState(productCountInCart || 1);
  const [inputQuantity, setinputQuantity] = useState(1);
  const cardRef = useRef(null);
  const [isModifying, setIsModifying] = useState(false);
  const [isMouseOnCard, setIsMouseOnCard] = useState(false);
  const [addButtonLabel, setAddButtonLabel] = useState("Add to cart");
  const dispatch = useDispatch();
  const isDiscountPresent = product?.discountPercentage > 0;
  useEffect(() => {
    setTimeout(() => setQuantity(() => productCountInCart || 1), 900);
  }, [productCountInCart]);

  const handleQuantityChange = (productCount = 1) => {
    setQuantity(() => productCount);
    if (!productInCart || productCount === productCountInCart) return;
    setIsModifying(true);
    setAddButtonLabel("Modify cart");
  };

  const resetToDefault = () => {
    setTimeout(() => {
      setQuantity(() => productCountInCart || 1);
      setIsModifying(false);
      setAddButtonLabel("Add to cart");
    }, 900);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cardRef.current && !cardRef.current.contains(event.target)) {
        resetToDefault();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [productInCart]);

  useEffect(() => {
    if (!isMouseOnCard && isModifying) {
      resetToDefault();
    }
  }, [isMouseOnCard, isModifying]);

  const canAddToCart = useMemo(() => {
    // If product is in cart, check if adding more would exceed stock
    if (productInCart) {
      return quantity <= productInCart?.stockLeft;
    }
    // If product is not in cart, allow adding only if stock is greater than 0
    return product?.stockLeft > 0;
  }, [cartItem, quantity, productInCart, product?.stockLeft]);

  const onAddToCart = () => {
    console.log(cartInputValue, "cartInputValue-11");
    let copyOfCartInput;
    if (cartInputValue.length === 1 && cartInputValue[0].id === "") {
      let updatedCart = [{ id: product?.id, qty: 1, input: false }];
      dispatch(setCartInputValue(updatedCart));
    }
    const existingProduct = cartInputValue?.find(
      (item) => item.id === product?.id
    );

    let updatedCart;
    if (existingProduct) {
      // If product exists, update its quantity
      updatedCart = cartInputValue.map((item) => {
        if (item.input) {
          return item.id === product?.id
            ? { ...item, qty: item.qty, input: false }
            : item;
        } else {
          return item.id === product?.id
            ? { ...item, qty: item.qty + 1 }
            : item;
        }
      });
    } else {
      // If product doesn't exist, add it
      updatedCart = [{ id: product?.id, qty: quantity, input: false }];
    }
    if (quantity <= product?.stockLeft) {
      let cartInputQty = updatedCart?.find((item) => item.id === product?.id);
      console.log(cartInputQty.qty, "cartInputQty.qty");
      dispatch(setCartInputValue(updatedCart));
      handleAddCart({
        productDetails: product,
        quantity:quantity||1,
        inputQuantity: cartInputQty.qty !== '' ? cartInputQty.qty : 1,
        localCartItems,
      });
      // Update button state if adding the last item
      if (quantity === product?.stockLeft) {
        setAddButtonLabel("Out of Stock");
      } else {
        setAddButtonLabel(`${quantity} item${quantity > 1 ? "s" : ""} added`);
      }

      setTimeout(() => {
        setQuantity(() => productCountInCart || 1);
        setAddButtonLabel("Add to cart");
      }, 900);
    }
  };

  return (
    <div
      className="w-full mb-4 md:mb-0"
      ref={cardRef}
      onMouseEnter={() => setIsMouseOnCard(true)}
      onMouseLeave={() => {
        setIsMouseOnCard(false);
        resetToDefault();
      }}
    >
      <header className="w-full h-[210px] xl:h-[310px] bg-midnightIndigo-100 rounded mb-4 flex items-center relative p-4 ">
        <div
          className={` absolute top-[10px] left-0  right-0 px-2 flex  ${
            product?.isFlash ? "justify-between" : "justify-start"
          } gap-1`}
        >
          {product?.isFlash !== null && (
            <div
              className="flex flex-row items-center justify-center gap-1 bg-system-orange rounded px-2 py-1"
              style={{ width: "72px" }}
            >
              <Flash className="h-5 w-5 text-system-crystalWhite" />
              <span className="text-system-crystalWhite mt-1">Sale</span>
            </div>
          )}
          <div className="flex flex-row gap-1 ml-auto">
            {product?.item_left !== null ? (
              <div className=" rounded px-2 py-1 bg-system-crystalWhite">
                <Caption
                  size="xs"
                  variant="regular"
                  className="text-system-warning flex items-center "
                >
                  <TriangleAlert className=" h-[20px] w-[20px] mr-1" />
                  {product?.item_left} left in Stock
                </Caption>
              </div>
            ) : (
              ""
            )}
            {isDiscountPresent && (
              <div className="bg-midnightIndigo-400 rounded hidden lg:block px-2 py-1">
                <Caption size="xs" variant="regular" className="text-white">
                  -{product?.discountPercentage}%
                </Caption>
              </div>
            )}
          </div>
        </div>

        <div className="  flex items-center justify-center overflow-hidden h-full w-full aspect-w-16 aspect-h-9  rounded">
          <Image
            alt={product?.img?.imageAlt}
            src={product?.img?.imagePath}
            width={250}
            height={250}
            className="h-full w-full object-contain "
          />
        </div>
      </header>

      <section className="">
        <Link
          href={`/product/${encodeURIComponent(product?.name)}`}
          onClick={() => dispatch(setSelectedProductId(product?.id))}
        >
          <div className="mb-1">
            <Caption size="xs" className="md:text-[13px] mb-1 truncate">
              {product?.modelNumber || "-"}
            </Caption>
            <Title
              size="xs"
              className="min-h-[46px]  line-clamp-2 leading-[22px]"
            >
              {product?.name}
            </Title>
          </div>
        </Link>

        <div className="w-full mb-2 lg:mt-0 flex flex-col lg:flex-row gap-2 justify-between lg:items-center">
          <div className="flex flex-col">
            {(isDiscountPresent || product?.isFlash) &&
              product?.price?.rawValue !== product?.isFlash && (
                <Caption className="line-through text-[15px] h-[20px] md:text-[14px]">
                  {product?.price?.formattedValue}
                </Caption>
              )}
            <Title size="sm" className="md:text-[16px]">
              {product?.isFlash == null
                ? product?.discountedPrice?.formattedValue
                : formatToUSD(product?.isFlash)}
            </Title>
          </div>
          <div className="w-full h-[35px] lg:h-[44px] lg:w-[108px]">
            <QuantityButton
              product={product}
              quantity={quantity}
              inStock={product?.stockLeft}
              onQuantityChange={handleQuantityChange}
            />
          </div>
        </div>

        <Button
          size="xl"
          disabled={!canAddToCart}
          className={`hover:!bg-midnightIndigo-400 hover:text-white border-system-grey hover:!border-midnightIndigo-400 text-[16px] 
              ${isModifying ? "ring-1 ring-midnightIndigo-400" : ""}`}
          variant="outline"
          onClick={onAddToCart}
        >
          {!addButtonLabel.startsWith("Add") &&
            !addButtonLabel.startsWith("Modify") && <Check className="mr-2" />}
          {addButtonLabel}
        </Button>
      </section>
    </div>
  );
}
